import { errorMessage, type RemoteKnowledge } from "./api/client";

export type ReviewDecision = "accept" | "reject";

/** Résultat d'une revue par lot : ce qui a réussi, et pour chaque échec la raison donnée par le hub. */
export interface ReviewOutcome {
  decision: ReviewDecision;
  succeeded: string[];
  failed: { itemId: string; message: string }[];
}

export const isSuspicious = (edit: RemoteKnowledge) => (edit.suspicious ?? []).length > 0;

/** Éléments qu'on peut cocher, une fois chacun : plusieurs sessions peuvent avoir édité le même. */
export function selectableIds(edits: RemoteKnowledge[]): string[] {
  return [...new Set(edits.flatMap((e) => (e.item_id ? [e.item_id] : [])))];
}

/**
 * Éléments cochés que la décision traite. Un élément dont une édition est suspecte ne s'accepte pas (le hub le refuse) :
 * coché, il ne part qu'avec un refus. Une coche sur un élément qui n'est plus en attente est ignorée.
 */
export function reviewTargets(edits: RemoteKnowledge[], selected: ReadonlySet<string>, decision: ReviewDecision): string[] {
  const suspicious = new Set(edits.filter(isSuspicious).map((e) => e.item_id));
  return selectableIds(edits).filter((id) => selected.has(id) && (decision === "reject" || !suspicious.has(id)));
}

export function toggleSelected(selected: ReadonlySet<string>, itemId: string): Set<string> {
  const next = new Set(selected);
  if (!next.delete(itemId)) next.add(itemId);
  return next;
}

export function allSelected(edits: RemoteKnowledge[], selected: ReadonlySet<string>): boolean {
  const ids = selectableIds(edits);
  return ids.length > 0 && ids.every((id) => selected.has(id));
}

/** « Tout sélectionner » : coche tout, ou décoche tout si tout l'était déjà. */
export function toggleAll(edits: RemoteKnowledge[], selected: ReadonlySet<string>): Set<string> {
  return allSelected(edits, selected) ? new Set() : new Set(selectableIds(edits));
}

/** Une requête par élément, l'une après l'autre ; un échec n'arrête pas les suivantes. */
export async function reviewBatch(itemIds: string[], decision: ReviewDecision, review: (itemId: string) => Promise<unknown>): Promise<ReviewOutcome> {
  const outcome: ReviewOutcome = { decision, succeeded: [], failed: [] };
  for (const itemId of itemIds) {
    try {
      await review(itemId);
      outcome.succeeded.push(itemId);
    } catch (error) {
      outcome.failed.push({ itemId, message: errorMessage(error) });
    }
  }
  return outcome;
}

export function outcomeSummary(outcome: ReviewOutcome): string {
  const done = `${outcome.succeeded.length} édition(s) ${outcome.decision === "accept" ? "acceptée(s)" : "refusée(s)"}`;
  return outcome.failed.length === 0 ? `${done}.` : `${done}, ${outcome.failed.length} en échec :`;
}
